import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaVideo, FaPlus } from 'react-icons/fa';
import api from '../utils/api';
import LiveBadge from './LiveBadge';

const LiveStreamsBar = () => {
  const navigate = useNavigate();
  const [liveStreams, setLiveStreams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLiveStreams = async () => {
      try {
        const response = await api.get('/live/active');
        setLiveStreams(response.data);
      } catch (error) {
        console.error('Error fetching live streams:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLiveStreams();

    // Poll for new live streams
    const interval = setInterval(fetchLiveStreams, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleGoLive = () => {
    navigate('/go-live');
  };

  const handleJoinStream = (streamId) => {
    navigate(`/live/${streamId}`);
  };

  if (isLoading && liveStreams.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border-b border-gray-200 py-4 px-4 mb-4">
      <div className="flex items-center space-x-4 overflow-x-auto scrollbar-hide">
        {/* PointLive Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleGoLive}
          className="flex-shrink-0 flex flex-col items-center space-y-1"
        >
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 p-0.5">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-pink-100 to-purple-100 flex items-center justify-center">
                  <FaVideo className="text-purple-500 text-xl" />
                </div>
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full p-1">
              <FaPlus className="text-white text-xs" />
            </div>
          </div>
          <span className="text-xs text-gray-600 font-medium font-arthaus">PointLive</span>
        </motion.button>

        {/* Live Streams */}
        {liveStreams.map((stream) => (
          <motion.button
            key={stream.id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleJoinStream(stream.id)}
            className="flex-shrink-0 flex flex-col items-center space-y-1"
          >
            <div className="relative">
              {/* Animated gradient ring */}
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 via-red-500 to-purple-500 p-0.5 animate-pulse">
                <div className="w-full h-full rounded-full bg-white p-0.5">
                  <img
                    src={stream.broadcaster_profile_picture || `https://ui-avatars.com/api/?name=${stream.broadcaster_username}`}
                    alt={stream.broadcaster_username}
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
              </div>
              {/* Live Badge */}
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                <LiveBadge size="xs" />
              </div>
            </div>
            <span className="text-xs text-gray-600 font-medium truncate max-w-16">
              {stream.broadcaster_username}
            </span>
          </motion.button>
        ))}

        {/* Empty State */}
        {liveStreams.length === 0 && (
          <div className="flex items-center space-x-2 text-gray-400 text-sm pl-4">
            <FaVideo />
            <span>No one is live right now. Be the first!</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStreamsBar;
